/* eslint-disable no-irregular-whitespace */
/* eslint-disable max-lines-per-function */
import { toNDigits } from '@mail/cross-sizes-utils';
import React, { type ReactElement, memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { overquotaBlockDate } from 'reactApp/appHelpers/featuresHelpers/features/overquotaBlockDate';
import {
    overquotaCommunicationPayPlacement,
    overquotaModalConfig,
    overquotaMoreInfoUrl,
    overquotaNewCommunication,
} from 'reactApp/appHelpers/featuresHelpers/features/overquotaCommunications';
import { PaymentUTM } from 'reactApp/constants/paymentUTM';
import { useBuyClickHandlers } from 'reactApp/hooks/useBuyClickHandlers';
import { sendDwhModal } from 'reactApp/modules/dwh/dwh.helpers';
import { getUserOverquotaState } from 'reactApp/modules/profile/profile.selectors';
import { historyPush } from 'reactApp/modules/router/router.module';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import Content from 'reactApp/ui/Content/Content';
import { Dialog } from 'reactApp/ui/Dialog/Dialog';
import { OverquotaSpaceBlock } from 'reactApp/ui/OverquotaSpaceBlock';
import { formatFileHistoryDate } from 'reactApp/utils/formatDate';
import { sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './OverquotaModal.css';
import { OverquotaBannerCtrl, sendDwhOverquota, sendTechOverqRadar } from './OverquotaModal.helpers';

const ACTION_NOT_ENOUGH_SPACE = 'not-enough-space';

const place = 'overquota_started';
const name = 'overquota_splash_new';

export type OverquotaModalProps = {
    onClose?: () => void;
};

export const OverquotaModal = memo(function OverquotaPopup({ onClose }: OverquotaModalProps): ReactElement | null {
    const dispatch = useDispatch();
    const storage = useSelector(getCurrentStorage);
    const { used, userTotalSpace } = useSelector(UserQuotaSelectors.getUserQuotaState);
    const overquota = useSelector(UserQuotaSelectors.getOverQuota);
    const { trashSize = 0, blockTime = undefined, state, clearTime } = useSelector(getUserOverquotaState);
    const theFirstStep = overquotaBlockDate && blockTime && state === 'started';
    const theSecondStep = overquotaBlockDate && blockTime && (state === 'blocked' || state === 'unblocked');

    const hasTrash = trashSize > 0;
    const hasClearButton = overquotaNewCommunication && overquotaModalConfig.clearButton;
    const showModal = Boolean(used && userTotalSpace);

    const { showPaywall } = useBuyClickHandlers({ paySource: 'mnz_overquota_modal' });

    const getModalName = useCallback(() => {
        if (state === 'full_blocked') {
            return 'mnz_overquota_after_94_day';
        }

        return 'overquota_splash_new';
    }, [state]);

    useEffect(() => {
        if (!showModal) {
            return;
        }

        sendTechOverqRadar({ state });

        sendDwhOverquota({ action: 'show', storage, place, name });

        emitAnalyticEvent(AnalyticEventNames.BLOCK_OVERQUOTA_VIEW);
        sendPaymentGa({
            action: ACTION_NOT_ENOUGH_SPACE,
            label: 'view',
        });

        sendDwhModal({ name: getModalName() });
    }, [showModal]);

    const title = useMemo(() => {
        if (overquotaNewCommunication) {
            if (overquotaBlockDate && blockTime && state === 'started') {
                return (
                    <>
                        <span>{formatFileHistoryDate(blockTime)}</span> доступ к файлам будет ограничен, но есть решение
                    </>
                );
            }

            if (overquotaBlockDate && clearTime && (state === 'unblocked' || state === 'full_blocked')) {
                return (
                    <>
                        <span>{formatFileHistoryDate(clearTime)}</span> ваши файлы будут удалены. Как не допустить
                    </>
                );
            }
        }

        if (state === 'full_blocked') {
            return 'Файлы будут удалены — как не допустить';
        }

        if (overquotaBlockDate && blockTime && state === 'started') {
            return (
                <>
                    <span>{formatFileHistoryDate(blockTime)}</span> доступ к файлам будет заблокирован, но есть решение
                </>
            );
        }

        if (overquotaBlockDate && clearTime && state === 'unblocked') {
            return (
                <>
                    <span>{formatFileHistoryDate(clearTime)}</span> ваши файлы будут удалены
                </>
            );
        }

        return 'Вашим файлам не хватает места';
    }, [blockTime, overquotaBlockDate, state]);

    const content = useMemo(() => {
        if (overquotaNewCommunication) {
            if (hasTrash) {
                return (
                    <>
                        Единое пространство Облака и Почты переполнено. Увеличьте его с подпиской Mail Space или очистите{' '}
                        <span>
                            {
                                toNDigits({
                                    bytes: trashSize,
                                    maxDigitsCount: 3,
                                }).value
                            }
                        </span>{' '}
                        в корзине
                    </>
                );
            }

            if (overquotaBlockDate && blockTime && state === 'started') {
                return (
                    <>
                        Единое пространство Облака и Почты переполнено на <span>{overquota.value}</span> — нельзя будет загружать, смотреть
                        и отправлять файлы. Увеличьте место с подпиской Mail Space.{' '}
                        <a href={overquotaMoreInfoUrl} target="_blank" rel="noreferrer">
                            Подробнее
                        </a>
                    </>
                );
            }

            if (overquotaBlockDate && clearTime && (state === 'unblocked' || state === 'full_blocked')) {
                return (
                    <>
                        Единое пространство Облака и Почты переполнено на <span>{overquota.value}</span> — увеличьте его с подпиской
                        Mail Space{hasClearButton ? ' или удалите лишнее' : ''}. Если ничего не сделать, оно автоматически очистится.{' '}
                        <a href={overquotaMoreInfoUrl} target="_blank" rel="noreferrer">
                            Подробнее
                        </a>
                    </>
                );
            }
        }

        if (hasTrash) {
            return (
                <>
                    Увеличьте место или очистите корзину. Файлы в ней занимают 
                    <span className={styles.quotaSize}>
                        {' '}
                        {
                            toNDigits({
                                bytes: trashSize,
                                maxDigitsCount: 3,
                            }).value
                        }
                    </span>
                     — этого хватит, чтобы сохранить доступ
                </>
            );
        }

        if (state === 'full_blocked') {
            return (
                <>
                    Память в Облаке и Почте переполнена. Удалите лишнее или увеличьте её с подпиской Mail Space. Если ничего не сделать,
                    место автоматически очистится.{' '}
                    <a href={overquotaMoreInfoUrl} target="_blank" rel="noreferrer">
                        Подробнее
                    </a>
                </>
            );
        }

        if (overquotaBlockDate && (blockTime || clearTime)) {
            return (
                <>
                    Пространство для хранения ваших файлов переполнено. Увеличьте его или очистите от лишнего.{' '}
                    <a href={overquotaMoreInfoUrl} target="_blank" rel="noreferrer">
                        Подробнее
                    </a>
                </>
            );
        }

        return (
            <>
                Скоро они{' '}
                <a href={overquotaMoreInfoUrl} target="_blank" rel="noreferrer">
                    будут удалены
                </a>
                . Увеличьте место, чтобы их сохранить, или очистите Облако от ненужных файлов
            </>
        );
    }, [overquota, hasTrash, blockTime, overquotaBlockDate, trashSize]);

    const clearButtonText = useMemo(() => {
        if (overquotaNewCommunication) {
            if (hasTrash) {
                return 'Очистить';
            }

            if (overquotaBlockDate && blockTime) {
                return 'Удалить лишнее';
            }
        }

        if (hasTrash) {
            return 'Перейти в корзину';
        }

        return 'Очистить';
    }, [hasTrash, blockTime, overquotaBlockDate]);

    const buttonTitle = useMemo(() => {
        if (overquotaNewCommunication) {
            if (overquotaBlockDate && blockTime && state === 'started') {
                return 'Снять ограничение';
            }

            if (overquotaBlockDate && clearTime && (state === 'unblocked' || state === 'full_blocked')) {
                return 'Увеличить место';
            }
        }

        return 'Увеличить место';
    }, [state]);

    const onPrimaryHandler = useCallback(() => {
        sendDwhOverquota({ action: 'click', storage, control: OverquotaBannerCtrl.add_space, place, name });

        let search = PaymentUTM.overquotaSplashNew;

        if (theFirstStep) {
            search = PaymentUTM.overquotaPopupBlockFirstStepIncreaseSpace;
        }

        if (theSecondStep) {
            search = PaymentUTM.overquotaPopupBlockSecondStepIncreaseSpace;
        }

        if (state === 'full_blocked') {
            search = PaymentUTM.overquotaFullBlockedNewModal;
        }

        sendDwhModal({ action: 'click', name: getModalName(), button_name: OverquotaBannerCtrl.add_space });

        if (overquotaCommunicationPayPlacement === 'paywall') {
            showPaywall();
        } else {
            dispatch(historyPush({ id: '/promo/quota', search: `?${search}` }));
        }

        onClose?.();

        sendTechOverqRadar({ state, action: 'click', bttnName: 'increase' });
    }, [onClose, storage, dispatch, state]);

    const onSecondaryHandler = useCallback(() => {
        sendDwhOverquota({ action: 'click', storage, control: OverquotaBannerCtrl.clear, place, name });

        let search = '';

        if (theFirstStep) {
            search = PaymentUTM.overquotaPopupBlockFirstStepClearSpace;
        }
        if (theSecondStep) {
            search = PaymentUTM.overquotaPopupBlockSecondStepClearSpace;
        }

        sendDwhModal({ action: 'click', name: getModalName(), button_name: OverquotaBannerCtrl.clear });

        if (hasTrash) {
            dispatch(
                historyPush({
                    id: EStorageType.trashbin,
                    search: `?${search}`,
                })
            );
        } else {
            dispatch(
                historyPush({
                    id: '/promo/quota',
                    search: `?${search}`,
                    hash: 'cleaner',
                })
            );
        }
        onClose?.();

        sendTechOverqRadar({ state, action: 'click', bttnName: 'clear' });
    }, [onClose, storage, dispatch, hasTrash]);

    const onCloseHandler = useCallback(() => {
        sendDwhOverquota({ action: 'click', storage, control: OverquotaBannerCtrl.closed, place, name });

        sendDwhModal({ action: 'close', name: getModalName() });

        onClose?.();
    }, [onClose, storage]);

    if (!showModal) {
        return null;
    }

    return (
        <Dialog
            open
            header={''}
            closeOnDimmerClick={false}
            id="overquota-alert"
            qaId="overquota-alert"
            onCancel={onCloseHandler}
            className={styles.root}
            size="tiny"
        >
            <Content isModal>
                <div className={styles.content}>
                    <div className={styles.imageWrapper}>
                        <OverquotaSpaceBlock />
                    </div>
                    <div className={styles.title}>{title}</div>
                    <div className={styles.text}>{content}</div>
                    <div className={styles.footerWrapper}>
                        <Button
                            className={styles.button}
                            data-name="freeze-renewal"
                            theme="octavius"
                            sizeMode={ButtonSizeMode.small}
                            onClick={onPrimaryHandler}
                            primary={overquotaNewCommunication}
                            error={!overquotaNewCommunication}
                        >
                            {buttonTitle}
                        </Button>
                        {hasClearButton && (
                            <Button
                                className={styles.button}
                                onClick={onSecondaryHandler}
                                theme="octavius"
                                sizeMode={ButtonSizeMode.small}
                                data-name="secondary"
                            >
                                {clearButtonText}
                            </Button>
                        )}
                    </div>
                </div>
            </Content>
        </Dialog>
    );
});

OverquotaModal.displayName = 'OverquotaModal';
